import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Section = 'business' | 'chats' | 'desktop' | 'dashboards'

interface AppShellState {
  section: Section
  setSection: (s: Section) => void
  aiPanelOpen: boolean
  toggleAiPanel: () => void
  setAiPanelOpen: (open: boolean) => void
  // Last visited paths per section, used to restore navigation when user
  // clicks Business OS / Chats sidebar icons.
  lastBusinessPath: string | null
  lastChatPath: string | null
  setLastBusinessPath: (path: string | null) => void
  setLastChatPath: (path: string) => void
  // OSPROD-63: last URL the user was on that was NOT a voice-channel
  // route. Used as the minimize-call target — clicking the Minimize
  // button drops the user back where they came from BEFORE joining the
  // call, instead of forcing them onto the Desktop dashboard (the prior
  // implementation set section='desktop' and parked everyone there,
  // which QA reported as "переход на вкладку Desktop happens, but it
  // shouldn't"). Persisted so it survives reloads.
  lastNonCallPath: string | null
  setLastNonCallPath: (path: string) => void
  // OSPROD-102: collapse the channel / secondary sidebar (Discord-style) so
  // the call / screen-share area widens. Persisted across reloads.
  secondarySidebarCollapsed: boolean
  setSecondarySidebarCollapsed: (collapsed: boolean) => void
  // W1.0: mobile two-level nav drawer. Transient UI — NOT persisted
  // (a reload must not reopen the drawer).
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void
}

export const useAppShellStore = create<AppShellState>()(
  persist(
    (set) => ({
      section: 'business',
      setSection: (section) => set({ section }),
      aiPanelOpen: false,
      toggleAiPanel: () => set((s) => ({ aiPanelOpen: !s.aiPanelOpen })),
      setAiPanelOpen: (aiPanelOpen) => set({ aiPanelOpen }),
      lastBusinessPath: null,
      lastChatPath: null,
      setLastBusinessPath: (lastBusinessPath) => set({ lastBusinessPath }),
      setLastChatPath: (lastChatPath) => set({ lastChatPath }),
      lastNonCallPath: null,
      setLastNonCallPath: (lastNonCallPath) => set({ lastNonCallPath }),
      secondarySidebarCollapsed: false,
      setSecondarySidebarCollapsed: (secondarySidebarCollapsed) => set({ secondarySidebarCollapsed }),
      mobileNavOpen: false,
      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
    }),
    {
      name: 'app-shell',
      partialize: (s) => ({
        section: s.section,
        lastBusinessPath: s.lastBusinessPath,
        lastChatPath: s.lastChatPath,
        lastNonCallPath: s.lastNonCallPath,
        secondarySidebarCollapsed: s.secondarySidebarCollapsed,
      }),
    }
  )
)
